import {
  Typography,
  Dropzone,
  SelectField,
} from '@flash-tecnologia/hros-web-ui-v2';
import { PageSubmitLayout } from 'components/Layout/PageBaseLayout';
import { InputLayout } from 'components/Layout/InputLayout';
import { useBffFetch } from 'api/getData';
import { RowContainer } from 'components/Containers';
import { dispatchToast, dispatchToastErrorHandler } from 'utils/dispatchToast';
import { RadioButton } from 'components/RadioButtom';
import { useNavigate } from 'react-router-dom';
import { CompanyDocumentSchema } from 'bff/src/application/schemas/company.schema';
import {
  DropzoneDocumentType,
  initialDocument,
  useDocuments,
} from './useDocuments';
import {
  getAccessToken,
  useSelectedCompany,
} from '@flash-tecnologia/hros-web-utility';
import { SetStateAction, useEffect } from 'react';
import { ComplianceDocumentType } from 'bff/src/domain/model/compliance.model';
import { ResponseError } from 'api/api.types';
import { CenteredSpinner } from 'components/Spinner/Spinner';

type DropzoneFiles = NonNullable<
  React.ComponentProps<typeof Dropzone>['initialFiles']
>;

type DropzoneFile = {
  id: string;
  data?: string | ArrayBuffer | null | undefined;
  progress: number;
  loaded: boolean;
  error: boolean;
  file: File;
  mime?: string;
};

type SignedUrlResponse = {
  url: string;
};

export const Documents = () => {
  const navigate = useNavigate();
  const { selectedCompany } = useSelectedCompany();
  const [documents, setDocument, validate] = useDocuments();

  const { loading, fetchBff } = useBffFetch<CompanyDocumentSchema, unknown>({
    path: `/v1/companies/${selectedCompany.id}/documents`,
    method: 'POST',
    onSuccess: () => {
      dispatchToast({
        title: 'Sucesso',
        content: 'Informações enviadas com sucesso!',
        type: 'success',
      });
      navigate('/');
    },
    onError: (err) => dispatchToastErrorHandler(err.statusCode),
  });

  useEffect(() => {
    setDocument.setContratoSocialDocData([]);
    setDocument.setLegalRepresetantDocType({ value: '', label: '', error: '' });
    setDocument.setLegalRepresetantDocFrontData(initialDocument);
    setDocument.setLegalRepresetantDocBackData(initialDocument);
    setDocument.setComprovanteEnderecoDocData(initialDocument);
    setDocument.setPowerOfAttorneyDocData(initialDocument);
    setDocument.setRentalAgreementDocData(initialDocument);
    setDocument.setIsShareholder(true);
    setDocument.setIsRented(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  const handleSocialContractFileChange = async (files: DropzoneFiles) => {
    if (files.length === 0) return;

    const token = await getAccessToken();

    let filesToUpload: DropzoneFiles = [];

    setDocument.setContratoSocialDocData((prev) => {
      const existingFileIds = new Set(prev.map((doc) => doc.id));
      const newFiles = files.filter(
        (attachment) => !existingFileIds.has(attachment.id),
      );

      if (newFiles.length === 0) {
        return prev;
      }

      filesToUpload = newFiles;

      const newFilesLoadingState = newFiles.map((attachment) => ({
        id: attachment.id,
        fileName: attachment.file.name,
        loading: true,
        error: '',
      }));

      return [...prev, ...newFilesLoadingState];
    });

    if (filesToUpload.length === 0) {
      return;
    }

    const updateFileState = (
      fileId: string,
      newState: Partial<DropzoneDocumentType>,
    ) => {
      setDocument.setContratoSocialDocData((prev) =>
        prev.map((doc) => (doc.id === fileId ? { ...doc, ...newState } : doc)),
      );
    };

    const uploadPromises = filesToUpload.map(async (attachment) => {
      if (isFileValid(attachment)) {
        try {
          const fileName = `${Date.now()}.${
            attachment.file.type.split('/')[1]
          }`;
          const signedUrlResponse = await fetch(
            `${process.env.BFF_URL}/v1/anti-fraud/v1/storage/${selectedCompany.id}/signed-url`,
            {
              method: 'POST',
              headers: {
                'x-flash-auth': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                documentType: 'SOCIAL_CONTRACT',
                fileName,
              }),
            },
          );
          if (!signedUrlResponse.ok) {
            updateFileState(attachment.id, {
              loading: false,
              error: 'Erro ao carregar o arquivo',
            });
            return;
          }
          const signedUrlResult: SignedUrlResponse =
            await signedUrlResponse.json();

          const s3Response = await fetch(signedUrlResult.url, {
            method: 'PUT',
            headers: {
              'Content-Type': attachment.file.type,
            },
            body: attachment.file,
          });

          if (!s3Response.ok) {
            updateFileState(attachment.id, {
              loading: false,
              error: 'Erro ao carregar o arquivo',
            });
            return;
          }
          updateFileState(attachment.id, { fileName, loading: false });
        } catch (error) {
          updateFileState(attachment.id, {
            loading: false,
            error: 'Erro ao carregar o arquivo',
          });
        }
      } else {
        updateFileState(attachment.id, {
          loading: false,
          error: 'Arquivo não é válido',
        });
      }
    });

    await Promise.all(uploadPromises);
  };

  const handleFilesChange = async (
    files: DropzoneFiles,
    documentType: ComplianceDocumentType,
    setFunction: (value: SetStateAction<DropzoneDocumentType>) => void,
  ) => {
    if (files.length === 0) return;

    const token = await getAccessToken();
    for (const attachment of files) {
      if (isFileValid(attachment)) {
        try {
          setFunction((prevState) => ({ ...prevState, loading: true }));
          const fileName = `${Date.now()}.${
            attachment.file.type.split('/')[1]
          }`;
          const signedUrlResponse = await fetch(
            process.env.BFF_URL +
              `/v1/anti-fraud/v1/storage/${selectedCompany.id}/signed-url`,
            {
              headers: {
                'x-flash-auth': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              method: 'POST',
              body: JSON.stringify({
                documentType,
                fileName,
              }),
            },
          );
          if (!signedUrlResponse.ok) {
            setFunction({
              fileName: undefined,
              loading: false,
              error: 'Erro ao carregar o arquivo',
            });
            break;
          }
          const signedUrlResult: SignedUrlResponse =
            await signedUrlResponse.json();

          const s3Response = await fetch(signedUrlResult.url, {
            method: 'PUT',
            headers: {
              'Content-Type': attachment.file.type,
            },
            body: attachment.file,
          });

          if (!s3Response.ok) {
            setFunction({
              fileName: undefined,
              loading: false,
              error: 'Erro ao carregar o arquivo',
            });
            break;
          }
          setFunction({ fileName, loading: false, error: '' });
        } catch (error) {
          setFunction({
            fileName: undefined,
            loading: false,
            error: 'Erro ao carregar o arquivo',
          });
          break;
        }
      } else {
        setFunction({
          fileName: undefined,
          loading: false,
          error: 'Arquivo não é válido',
        });
      }
    }
  };

  const handleSubmit = () => {
    const contratoSocialDocs = documents.contratoSocialDocData.map((doc) => ({
      fileName: doc.fileName?.toString() ?? '',
      type: 'SOCIAL_CONTRACT' as const,
    }));

    const documentToSend: CompanyDocumentSchema = [
      ...contratoSocialDocs,
      {
        fileName:
          documents.legalRepresetantDocBackData.fileName?.toString() ?? '',
        type: 'LEGAL_REPRESENTANT_BACK',
        legalRepresentativeDocumentType:
          documents.legalRepresetantDocType.value || undefined,
        isShareholder: documents.isShareholder,
      },
      {
        fileName:
          documents.legalRepresetantDocFrontData.fileName?.toString() ?? '',
        type: 'LEGAL_REPRESENTANT_FRONT',
        legalRepresentativeDocumentType:
          documents.legalRepresetantDocType.value || undefined,
        isShareholder: documents.isShareholder,
      },
    ];

    if (documents.comprovanteEnderecoDocData.fileName) {
      documentToSend.push({
        fileName:
          documents.comprovanteEnderecoDocData.fileName?.toString() ?? '',
        type: 'ADDRESS',
        isRented: documents.isRented,
      });
    }
    if (!documents.isShareholder && documents.powerOfAttorneyDocData.fileName) {
      documentToSend.push({
        fileName: documents.powerOfAttorneyDocData.fileName?.toString() ?? '',
        type: 'POWER_ATTORNEY',
      });
    }
    if (documents.isRented && documents.rentalAgreementDocData.fileName) {
      documentToSend.push({
        fileName: documents.rentalAgreementDocData.fileName?.toString() ?? '',
        type: 'RENTAL_CONTRACT',
        isRented: documents.isRented,
      });
    }

    if (validate()) {
      fetchBff(documentToSend);
    } else {
      dispatchToast({
        title: 'Aviso',
        content: 'Existem documentos obrigatórios que estão faltando!',
        type: 'warning',
      });
    }
  };

  const isFileValid = (file: DropzoneFile) => {
    return file && !file.error && file.loaded;
  };

  const isUploading = documents.contratoSocialDocData.some(
    (doc) => doc.loading,
  );

  return (
    <PageSubmitLayout
      pageTitle="Arquivos da empresa e do representante legal"
      descriptionTitle="Documentos adicionais"
      descriptionSubTitle="Envie os documentos complementares para regularização da empresa na plataforma."
      onSubmit={() => handleSubmit()}
      onCancel={() => navigate('/')}
      loading={loading}
    >
      <InputLayout
        title="Documentação Societária"
        subTitle={
          <div>
            Envie o documento formalizando a criação da empresa e definindo as
            regras entre os sócios.
            <br />
            *Caso tenha dúvidas sobre qual documento mandar, acesse a FAQ em
            "clique aqui" do lado esquerdo da tela.
          </div>
        }
      >
        <Typography
          variant="body3"
          style={{ fontWeight: '600', color: '#53464F' }}
        >
          Adicionar documentação societária
        </Typography>
        {isUploading && (
          <div style={{ height: '150px' }}>
            <CenteredSpinner size={48} />
          </div>
        )}
        <Dropzone
          key={`contratoSocial-${selectedCompany.id}`}
          title="Envie seu arquivo"
          accept={['jpg', 'jpeg', 'pdf', 'png']}
          multiple={true}
          maxNumberOfFiles={5}
          style={{
            marginTop: '5px',
            ...(isUploading ? { display: 'none' } : { display: 'block' }),
          }}
          onChange={(files) => handleSocialContractFileChange(files)}
          onRemove={(id) => {
            setDocument.setContratoSocialDocData((prevFIles) =>
              prevFIles.filter((file) => file.id !== id),
            );
          }}
        />
      </InputLayout>
      <InputLayout
        title="Documento do representante legal"
        subTitle="O representante legal faz parte do quadro societário da empresa?"
      >
        <RowContainer style={{ marginTop: '15px', marginLeft: '-10px' }}>
          <RadioButton
            label="Sim, faz parte"
            checked={documents.isShareholder}
            value={documents.isShareholder}
            onChange={() => {
              setDocument.setPowerOfAttorneyDocData(initialDocument);
              setDocument.setIsShareholder(true);
            }}
          />
          <RadioButton
            label="Não faz parte"
            checked={!documents.isShareholder}
            value={!documents.isShareholder}
            onChange={() => {
              setDocument.setPowerOfAttorneyDocData(initialDocument);
              setDocument.setIsShareholder(false);
            }}
          />
        </RowContainer>
        <SelectField
          label="Tipo de documento *"
          value={documents.legalRepresetantDocType}
          style={{ marginTop: '15px' }}
          fullWidth
          options={[
            { value: 'RG', label: 'RG' },
            { value: 'CNH', label: 'CNH' },
            { value: 'PASSPORT', label: 'Passaporte brasileiro' },
            { value: 'RNE/RNM', label: 'RNE/RNM' },
          ]}
          onSelectChange={(_, option) => {
            setDocument.setLegalRepresetantDocType(option);
            if (option.value === 'PASSPORT') {
              setDocument.setLegalRepresetantDocBackData(initialDocument);
            }
          }}
          error={!!documents.legalRepresetantDocType.error}
          helperText={documents.legalRepresetantDocType.error}
        />
        <Typography
          variant="body3"
          style={{ marginTop: '25px', fontWeight: '600', color: '#53464F' }}
        >
          Adicionar foto da frente
        </Typography>
        {documents.legalRepresetantDocFrontData.loading && (
          <div style={{ height: '150px' }}>
            <CenteredSpinner size={48} />
          </div>
        )}
        <Dropzone
          key={`legalRepresetantFront-${selectedCompany.id}`}
          title="Envie seu arquivo"
          accept={['jpg', 'jpeg', 'pdf', 'png']}
          style={{
            marginTop: '5px',
            ...(documents.legalRepresetantDocFrontData.loading
              ? { display: 'none' }
              : { display: 'flex' }),
          }}
          onChange={(files) =>
            handleFilesChange(
              files,
              'LEGAL_REPRESENTANT_FRONT',
              setDocument.setLegalRepresetantDocFrontData,
            )
          }
          onRemove={() =>
            setDocument.setLegalRepresetantDocFrontData(initialDocument)
          }
        />
        {documents.legalRepresetantDocType.value !== 'PASSPORT' && (
          <>
            <Typography
              variant="body3"
              style={{ marginTop: '25px', fontWeight: '600', color: '#53464F' }}
            >
              Adicionar foto do verso
            </Typography>
            {documents.legalRepresetantDocBackData.loading && (
              <div style={{ height: '150px' }}>
                <CenteredSpinner size={48} />
              </div>
            )}
            <Dropzone
              key={`legalRepresetantBack-${selectedCompany.id}`}
              title="Envie seu arquivo"
              accept={['jpg', 'jpeg', 'pdf', 'png']}
              style={{
                marginTop: '5px',
                ...(documents.legalRepresetantDocBackData.loading
                  ? { display: 'none' }
                  : { display: 'flex' }),
              }}
              onChange={(files) =>
                handleFilesChange(
                  files,
                  'LEGAL_REPRESENTANT_BACK',
                  setDocument.setLegalRepresetantDocBackData,
                )
              }
              onRemove={() =>
                setDocument.setLegalRepresetantDocBackData(initialDocument)
              }
            />
          </>
        )}

        {!documents.isShareholder && (
          <>
            <Typography
              variant="body3"
              style={{ marginTop: '25px', fontWeight: '600', color: '#53464F' }}
            >
              Foto da procuração do representante legal
            </Typography>
            {documents.powerOfAttorneyDocData.loading && (
              <div style={{ height: '150px' }}>
                <CenteredSpinner size={48} />
              </div>
            )}
            <Dropzone
              key={`PowerOfAttorney-${selectedCompany.id}`}
              title="Envie seu arquivo"
              accept={['jpg', 'jpeg', 'pdf', 'png']}
              style={{
                marginTop: '5px',
                ...(documents.powerOfAttorneyDocData.loading
                  ? { display: 'none' }
                  : { display: 'flex' }),
              }}
              onChange={(files) =>
                handleFilesChange(
                  files,
                  'POWER_ATTORNEY',
                  setDocument.setPowerOfAttorneyDocData,
                )
              }
              onRemove={() =>
                setDocument.setPowerOfAttorneyDocData(initialDocument)
              }
            />
          </>
        )}
      </InputLayout>

      <InputLayout
        title="Comprovante de endereço"
        subTitle="O imóvel onde está localizada a empresa é alugado?"
        optional
      >
        <RowContainer style={{ marginTop: '15px', marginLeft: '-10px' }}>
          <RadioButton
            label="Sim, é alugado"
            checked={documents.isRented}
            value={documents.isRented}
            onChange={() => {
              setDocument.setRentalAgreementDocData(initialDocument);
              setDocument.setIsRented(true);
            }}
          />
          <RadioButton
            label="Não é alugado"
            checked={!documents.isRented}
            value={!documents.isRented}
            onChange={() => {
              setDocument.setRentalAgreementDocData(initialDocument);
              setDocument.setIsRented(false);
            }}
          />
        </RowContainer>

        <Typography
          variant="body3"
          style={{ marginTop: '15px', fontWeight: '600', color: '#53464F' }}
        >
          Adicionar foto do comprovante de endereço. O arquivo precisa ser
          referente a uma conta de consumo (água, luz, telefone, internet, gás)
          emitido a menos de 3 meses.
        </Typography>
        {documents.comprovanteEnderecoDocData.loading && (
          <div style={{ height: '150px' }}>
            <CenteredSpinner size={48} />
          </div>
        )}
        <Dropzone
          key={`comprovanteEndereco-${selectedCompany.id}`}
          title="Envie seu arquivo"
          accept={['jpg', 'jpeg', 'pdf', 'png']}
          style={{
            marginTop: '5px',
            ...(documents.comprovanteEnderecoDocData.loading
              ? { display: 'none' }
              : { display: 'flex' }),
          }}
          onChange={(files) =>
            handleFilesChange(
              files,
              'ADDRESS',
              setDocument.setComprovanteEnderecoDocData,
            )
          }
          onRemove={() =>
            setDocument.setComprovanteEnderecoDocData(initialDocument)
          }
        />
        {documents.isRented && (
          <>
            <Typography
              variant="body3"
              style={{ marginTop: '15px', fontWeight: '600', color: '#53464F' }}
            >
              Adicionar foto do contrato de locação
            </Typography>
            {documents.rentalAgreementDocData.loading && (
              <div style={{ height: '150px' }}>
                <CenteredSpinner size={48} />
              </div>
            )}
            <Dropzone
              key={`rentalAgreement-${selectedCompany.id}`}
              title="Envie seu arquivo"
              accept={['jpg', 'jpeg', 'pdf', 'png']}
              style={{
                marginTop: '5px',
                ...(documents.rentalAgreementDocData.loading
                  ? { display: 'none' }
                  : { display: 'flex' }),
              }}
              onChange={(files) =>
                handleFilesChange(
                  files,
                  'RENTAL_CONTRACT',
                  setDocument.setRentalAgreementDocData,
                )
              }
              onRemove={() =>
                setDocument.setRentalAgreementDocData(initialDocument)
              }
            />
          </>
        )}
      </InputLayout>
    </PageSubmitLayout>
  );
};
